import { useEffect } from 'react';

/**
 * Dismiss an overlay the way people expect: a press anywhere outside it, or
 * Escape, puts it back to its resting state.
 *
 * Listens on `pointerdown` in the capture phase rather than `click`, so the
 * overlay collapses on press — before the click reaches whatever was sitting
 * underneath it. Does nothing while `active` is false, so there is no listener
 * on the document for a closed overlay.
 *
 * @param active  whether the overlay is currently open
 * @param ref     element that counts as "inside"
 * @param onDismiss called once when the user presses outside or hits Escape
 */
export function useDismissable(active, ref, onDismiss) {
  useEffect(() => {
    if (!active) return undefined;

    const onPointerDown = (e) => {
      if (!ref.current?.contains(e.target)) onDismiss();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onDismiss();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [active, ref, onDismiss]);
}
